"use client";

import { zodResolver } from "@hookform/resolvers/zod";
import { useTranslations } from "next-intl";
import { useState, useTransition } from "react";
import { useForm } from "react-hook-form";

import { login } from "@/actions/login";
import { PasswordField } from "@/components/auth/PasswordField";
import { FormError } from "@/components/FormError";
import { FormSuccess } from "@/components/FormSuccess";
import { Button, buttonVariants } from "@/components/ui/button";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "@/components/ui/input-otp";
import { Link } from "@/i18n/routing";
import { cn } from "@/lib/utils";
import { LoginSchema, TLoginSchema } from "@/schemas";

export const LoginForm = () => {
    const t = useTranslations("auth");

    const [ showTwoFactor, setShowTwoFactor ] = useState(false);
    const [ error, setError ] = useState<string | undefined>("");
    const [ success, setSuccess ] = useState<string | undefined>("");
    const [ isPending, startTransition ] = useTransition();

    const formSchema = LoginSchema(t);
    const form = useForm<TLoginSchema>({
        resolver: zodResolver(formSchema),
        defaultValues: {
            email: "",
            password: "",
        },
    });

    const onSubmit = (values: TLoginSchema) => {
        setError("");
        setSuccess("");
        startTransition(() => {
            login(values)
                .then(data => {
                    if (data.error) {
                        setError(data.error);
                    }

                    if (data.success) {
                        if (!data.twoFactor) form.reset();
                        setSuccess(data.success);
                    }

                    if (data.twoFactor) {
                        setShowTwoFactor(true);
                        setTimeout(() => form.setValue("code", ""), 1);
                    }
                });
        });
    };

    return (
        <Form { ...form }>
            <form onSubmit={ form.handleSubmit(onSubmit) } className="space-y-6">
                <div className="space-y-4">
                    {showTwoFactor ? (
                        <>
                            <FormField
                                control={ form.control }
                                name="code"
                                render={ ({ field }) => (
                                    <FormItem>
                                        <FormLabel>{t("2FA.2FAcodeLabel")}</FormLabel>
                                        <FormControl>
                                            <InputOTP maxLength={ 6 } { ...field } disabled={ isPending }>
                                                <InputOTPGroup>
                                                    <InputOTPSlot index={ 0 } />
                                                    <InputOTPSlot index={ 1 } />
                                                    <InputOTPSlot index={ 2 } />
                                                    <InputOTPSlot index={ 3 } />
                                                    <InputOTPSlot index={ 4 } />
                                                    <InputOTPSlot index={ 5 } />
                                                </InputOTPGroup>
                                            </InputOTP>
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                ) }
                            />
                        </>
                    ) : (
                        <>
                            <FormField
                                control={ form.control }
                                name="email"
                                render={ ({ field }) => (
                                    <FormItem>
                                        <FormLabel>{t("common.emailLabel")}</FormLabel>
                                        <FormControl>
                                            <Input { ...field } disabled={ isPending } type="email" autoComplete="email" />
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                ) }
                            />
                            <FormField
                                control={ form.control }
                                name="password"
                                render={ ({ field }) => (
                                    <FormItem>
                                        <FormLabel>{t("common.passwordLabel")}</FormLabel>
                                        <FormControl>
                                            <PasswordField { ...field } isPending={ isPending } autoComplete="current-password" />
                                        </FormControl>
                                        <Link
                                            href="forgot-password"
                                            className={ cn(buttonVariants({ variant: "link", size: "sm" }), "px-0 font-normal") }
                                        >
                                            {t("login.forgotPassword")}
                                        </Link>
                                        <FormMessage />
                                    </FormItem>
                                ) }
                            />
                        </>
                    )}
                </div>
                <FormError message={ error } />
                <FormSuccess message={ success } />
                <Button disabled={ isPending } type="submit" className="w-full">
                    {t("login.submitButton")}
                </Button>
            </form>
        </Form>
    );
};
